import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './SusuModal.css';

/**
 * SusuModal is a drop-in replacement for Semantic UI Modal.
 * It provides a custom, modern aesthetic while supporting similar props.
 */
const SusuModal = ({ 
  open, 
  onClose, 
  size = 'small', // 'mini', 'tiny', 'small', 'large', 'fullscreen'
  closeIcon = false, 
  className = '', 
  style = {},
  children 
}) => {
  const overlayRef = useRef(null);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current && onClose) {
      onClose();
    }
  };

  // Extract Header, Content, Actions from children if they exist
  let header = null;
  let content = null;
  let actions = null;
  const otherChildren = [];

  React.Children.forEach(children, child => {
    if (!React.isValidElement(child)) {
      otherChildren.push(child);
      return;
    }
    if (child.type === SusuModal.Header) header = child;
    else if (child.type === SusuModal.Content) content = child;
    else if (child.type === SusuModal.Actions) actions = child;
    else otherChildren.push(child);
  });

  return ReactDOM.createPortal(
    <div 
      className="susu-modal-overlay" 
      ref={overlayRef} 
      onClick={handleOverlayClick}
      style={{ zIndex: 100000 }} // Ensure it stays above semantic ui elements
    >
      <div 
        className={`susu-modal-container size-${size} ${className}`}
        style={style}
      >
        {/* Header */}
        {header ? (
          <div className="susu-modal-header" style={header.props.style}>
            <div className="susu-modal-header-content">
              {header.props.children}
            </div>
            {closeIcon && (
              <button className="susu-modal-close-btn" onClick={onClose} type="button">
                &#x2715;
              </button>
            )}
          </div>
        ) : closeIcon ? (
          // If no header but closeIcon is true, render a minimal header
          <div className="susu-modal-header" style={{ borderBottom: 'none', paddingBottom: 0 }}>
            <div className="susu-modal-header-content"></div>
            <button className="susu-modal-close-btn" onClick={onClose} type="button">
              &#x2715;
            </button>
          </div>
        ) : null}

        {/* Content */}
        {content ? (
          <div className={`susu-modal-content ${content.props.scrolling ? 'scrolling' : ''}`} style={content.props.style}>
            {content.props.children}
          </div>
        ) : null}

        {/* Other Unstructured Children */}
        {otherChildren.length > 0 && (
          <div className="susu-modal-content">
            {otherChildren}
          </div>
        )}

        {/* Actions */}
        {actions ? (
          <div className="susu-modal-actions" style={actions.props.style}>
            {actions.props.children}
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
};

SusuModal.Header = ({ children }) => <>{children}</>;
SusuModal.Content = ({ children }) => <>{children}</>;
SusuModal.Actions = ({ children }) => <>{children}</>;

export default SusuModal;
